export const CONTENT_CALENDAR_STATUSES =
  [
    "planned",
    "preparation_ready",
    "article_linked",
    "skipped",
    "cancelled",
  ] as const;

export const CONTENT_CALENDAR_PRIORITIES =
  [
    "low",
    "normal",
    "high",
    "urgent",
  ] as const;

export type ContentCalendarStatus =
  typeof CONTENT_CALENDAR_STATUSES[number];

export type ContentCalendarPriority =
  typeof CONTENT_CALENDAR_PRIORITIES[number];

export interface ContentCalendarItem {
  id:
    string;

  topic:
    string;

  topicKey:
    string;

  workingTitle:
    string;

  contentCategory:
    string;

  priority:
    ContentCalendarPriority;

  targetPublishAt:
    string;

  assignedAuthor:
    string |
    null;

  researchNotes:
    string |
    null;

  preferredCta:
    string |
    null;

  featuredImageRequirements:
    string |
    null;

  status:
    ContentCalendarStatus;

  articleId:
    string |
    null;

  skipReason:
    string |
    null;

  preparationReadyAt:
    string |
    null;

  articleLinkedAt:
    string |
    null;

  skippedAt:
    string |
    null;

  cancelledAt:
    string |
    null;

  createdAt:
    string;

  updatedAt:
    string;
}

export interface ContentCalendarListFilters {
  status?:
    ContentCalendarStatus;

  priority?:
    ContentCalendarPriority;

  limit?:
    number;
}

export interface ContentCalendarListResponse {
  ok:
    true;

  count:
    number;

  calendarItems:
    ContentCalendarItem[];
}

export interface ContentCalendarItemResponse {
  ok:
    true;

  calendarItem:
    ContentCalendarItem;
}

function isRecord(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value ===
      "object" &&
    value !==
      null &&
    !Array.isArray(
      value,
    )
  );
}

function readRequiredString(
  value: unknown,
  fieldName: string,
): string {
  if (
    typeof value !==
      "string" ||
    !value.trim()
  ) {
    throw new Error(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value;
}

function readNullableString(
  value: unknown,
  fieldName: string,
): string | null {
  if (
    value ===
    null
  ) {
    return null;
  }

  return readRequiredString(
    value,
    fieldName,
  );
}

function isCalendarStatus(
  value: unknown,
): value is ContentCalendarStatus {
  return (
    typeof value ===
      "string" &&
    CONTENT_CALENDAR_STATUSES.includes(
      value as ContentCalendarStatus,
    )
  );
}

function isCalendarPriority(
  value: unknown,
): value is ContentCalendarPriority {
  return (
    typeof value ===
      "string" &&
    CONTENT_CALENDAR_PRIORITIES.includes(
      value as ContentCalendarPriority,
    )
  );
}

export function parseContentCalendarItem(
  value: unknown,
): ContentCalendarItem {
  if (
    !isRecord(
      value,
    )
  ) {
    throw new Error(
      "A calendar item must be an object.",
    );
  }

  if (
    !isCalendarPriority(
      value.priority,
    )
  ) {
    throw new Error(
      "Calendar priority is invalid.",
    );
  }

  if (
    !isCalendarStatus(
      value.status,
    )
  ) {
    throw new Error(
      "Calendar status is invalid.",
    );
  }

  return {
    id:
      readRequiredString(
        value.id,
        "id",
      ),

    topic:
      readRequiredString(
        value.topic,
        "topic",
      ),

    topicKey:
      readRequiredString(
        value.topicKey,
        "topicKey",
      ),

    workingTitle:
      readRequiredString(
        value.workingTitle,
        "workingTitle",
      ),

    contentCategory:
      readRequiredString(
        value.contentCategory,
        "contentCategory",
      ),

    priority:
      value.priority,

    targetPublishAt:
      readRequiredString(
        value.targetPublishAt,
        "targetPublishAt",
      ),

    assignedAuthor:
      readNullableString(
        value.assignedAuthor,
        "assignedAuthor",
      ),

    researchNotes:
      readNullableString(
        value.researchNotes,
        "researchNotes",
      ),

    preferredCta:
      readNullableString(
        value.preferredCta,
        "preferredCta",
      ),

    featuredImageRequirements:
      readNullableString(
        value.featuredImageRequirements,
        "featuredImageRequirements",
      ),

    status:
      value.status,

    articleId:
      readNullableString(
        value.articleId,
        "articleId",
      ),

    skipReason:
      readNullableString(
        value.skipReason,
        "skipReason",
      ),

    preparationReadyAt:
      readNullableString(
        value.preparationReadyAt,
        "preparationReadyAt",
      ),

    articleLinkedAt:
      readNullableString(
        value.articleLinkedAt,
        "articleLinkedAt",
      ),

    skippedAt:
      readNullableString(
        value.skippedAt,
        "skippedAt",
      ),

    cancelledAt:
      readNullableString(
        value.cancelledAt,
        "cancelledAt",
      ),

    createdAt:
      readRequiredString(
        value.createdAt,
        "createdAt",
      ),

    updatedAt:
      readRequiredString(
        value.updatedAt,
        "updatedAt",
      ),
  };
}

export function parseContentCalendarListResponse(
  value: unknown,
): ContentCalendarListResponse {
  if (
    !isRecord(
      value,
    ) ||
    value.ok !==
      true ||
    !Array.isArray(
      value.calendarItems,
    )
  ) {
    throw new Error(
      "The calendar list response is invalid.",
    );
  }

  const calendarItems =
    value.calendarItems.map(
      parseContentCalendarItem,
    );

  if (
    typeof value.count !==
      "number" ||
    !Number.isInteger(
      value.count,
    ) ||
    value.count <
      0 ||
    value.count !==
      calendarItems.length
  ) {
    throw new Error(
      "Calendar count does not match the returned items.",
    );
  }

  return {
    ok:
      true,

    count:
      value.count,

    calendarItems,
  };
}

export function parseContentCalendarItemResponse(
  value: unknown,
): ContentCalendarItemResponse {
  if (
    !isRecord(
      value,
    ) ||
    value.ok !==
      true
  ) {
    throw new Error(
      "The calendar item response is invalid.",
    );
  }

  return {
    ok:
      true,

    calendarItem:
      parseContentCalendarItem(
        value.calendarItem,
      ),
  };
}

async function parseApiResponse(
  response: Response,
): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new Error(
      "The dashboard received invalid calendar API data.",
    );
  }
}

function readApiError(
  body: unknown,
  fallback: string,
): string {
  if (
    isRecord(
      body,
    ) &&
    typeof body.error ===
      "string"
  ) {
    return body.error;
  }

  return fallback;
}

export async function fetchContentCalendarItems(
  filters: ContentCalendarListFilters = {},
  signal?: AbortSignal,
): Promise<ContentCalendarListResponse> {
  const searchParams =
    new URLSearchParams();

  if (
    filters.status
  ) {
    searchParams.set(
      "status",
      filters.status,
    );
  }

  if (
    filters.priority
  ) {
    searchParams.set(
      "priority",
      filters.priority,
    );
  }

  searchParams.set(
    "limit",
    String(
      filters.limit ??
      200,
    ),
  );

  const response =
    await fetch(
      `/api/content-calendar?${searchParams.toString()}`,
      {
        method:
          "GET",

        headers: {
          Accept:
            "application/json",
        },

        cache:
          "no-store",

        signal,
      },
    );

  const body =
    await parseApiResponse(
      response,
    );

  if (
    !response.ok
  ) {
    throw new Error(
      readApiError(
        body,
        `Calendar request failed with HTTP ${response.status}.`,
      ),
    );
  }

  return parseContentCalendarListResponse(
    body,
  );
}

export async function fetchContentCalendarItem(
  id: string,
  signal?: AbortSignal,
): Promise<ContentCalendarItemResponse> {
  const normalizedId =
    id.trim();

  if (
    !normalizedId
  ) {
    throw new Error(
      "Calendar item ID is required.",
    );
  }

  const response =
    await fetch(
      `/api/content-calendar/${encodeURIComponent(normalizedId)}`,
      {
        method:
          "GET",

        headers: {
          Accept:
            "application/json",
        },

        cache:
          "no-store",

        signal,
      },
    );

  const body =
    await parseApiResponse(
      response,
    );

  if (
    !response.ok
  ) {
    throw new Error(
      readApiError(
        body,
        `Calendar item request failed with HTTP ${response.status}.`,
      ),
    );
  }

  return parseContentCalendarItemResponse(
    body,
  );
}

export interface CreateContentCalendarItemInput {
  topic:
    string;

  workingTitle:
    string;

  contentCategory:
    string;

  priority:
    ContentCalendarPriority;

  targetPublishAt:
    string;

  assignedAuthor?:
    string;

  researchNotes?:
    string;

  preferredCta?:
    string;

  featuredImageRequirements?:
    string;

  notes?:
    string;
}

export interface ContentCalendarCreatedResponse {
  ok:
    true;

  mode:
    "content-calendar-item-created";

  calendarItem:
    ContentCalendarItem;
}

export function parseContentCalendarCreatedResponse(
  value: unknown,
): ContentCalendarCreatedResponse {
  if (
    !isRecord(
      value,
    ) ||
    value.ok !==
      true ||
    value.mode !==
      "content-calendar-item-created"
  ) {
    throw new Error(
      "The calendar creation response is invalid.",
    );
  }

  return {
    ok:
      true,

    mode:
      "content-calendar-item-created",

    calendarItem:
      parseContentCalendarItem(
        value.calendarItem,
      ),
  };
}

function optionalRequestText(
  value: string | undefined,
): string | undefined {
  const normalized =
    value?.trim();

  return normalized
    ? normalized
    : undefined;
}

export async function createContentCalendarItem(
  input: CreateContentCalendarItemInput,
  signal?: AbortSignal,
): Promise<ContentCalendarCreatedResponse> {
  const response =
    await fetch(
      "/api/content-calendar",
      {
        method:
          "POST",

        headers: {
          Accept:
            "application/json",

          "Content-Type":
            "application/json",
        },

        cache:
          "no-store",

        signal,

        body:
          JSON.stringify({
            topic:
              input.topic.trim(),

            workingTitle:
              input.workingTitle.trim(),

            contentCategory:
              input.contentCategory.trim(),

            priority:
              input.priority,

            targetPublishAt:
              input.targetPublishAt,

            assignedAuthor:
              optionalRequestText(
                input.assignedAuthor,
              ),

            researchNotes:
              optionalRequestText(
                input.researchNotes,
              ),

            preferredCta:
              optionalRequestText(
                input.preferredCta,
              ),

            featuredImageRequirements:
              optionalRequestText(
                input.featuredImageRequirements,
              ),

            notes:
              optionalRequestText(
                input.notes,
              ),
          }),
      },
    );

  const body =
    await parseApiResponse(
      response,
    );

  if (
    !response.ok
  ) {
    throw new Error(
      readApiError(
        body,
        `Calendar creation failed with HTTP ${response.status}.`,
      ),
    );
  }

  return parseContentCalendarCreatedResponse(
    body,
  );
}

export interface MarkContentCalendarPreparationReadyInput {
  calendarItemId:
    string;

  notes?:
    string;
}

export interface ContentCalendarPreparationReadyResponse {
  ok:
    true;

  mode:
    "content-calendar-preparation-ready";

  calendarItem:
    ContentCalendarItem;
}

export function parseContentCalendarPreparationReadyResponse(
  value: unknown,
): ContentCalendarPreparationReadyResponse {
  if (
    !isRecord(
      value,
    ) ||
    value.ok !==
      true ||
    value.mode !==
      "content-calendar-preparation-ready"
  ) {
    throw new Error(
      "The preparation-ready response is invalid.",
    );
  }

  const calendarItem =
    parseContentCalendarItem(
      value.calendarItem,
    );

  if (
    calendarItem.status !==
      "preparation_ready"
  ) {
    throw new Error(
      "The preparation-ready response contains an unexpected calendar status.",
    );
  }

  return {
    ok:
      true,

    mode:
      "content-calendar-preparation-ready",

    calendarItem,
  };
}

export async function markContentCalendarPreparationReady(
  input:
    MarkContentCalendarPreparationReadyInput,

  signal?:
    AbortSignal,
): Promise<ContentCalendarPreparationReadyResponse> {
  const calendarItemId =
    input.calendarItemId.trim();

  if (
    !calendarItemId
  ) {
    throw new Error(
      "Calendar item ID is required.",
    );
  }

  const response =
    await fetch(
      `/api/content-calendar/${encodeURIComponent(
        calendarItemId,
      )}/preparation-ready`,
      {
        method:
          "POST",

        headers: {
          Accept:
            "application/json",

          "Content-Type":
            "application/json",
        },

        cache:
          "no-store",

        signal,

        body:
          JSON.stringify({
            notes:
              optionalRequestText(
                input.notes,
              ),
          }),
      },
    );

  const body =
    await parseApiResponse(
      response,
    );

  if (
    !response.ok
  ) {
    throw new Error(
      readApiError(
        body,
        `Preparation-ready transition failed with HTTP ${response.status}.`,
      ),
    );
  }

  return parseContentCalendarPreparationReadyResponse(
    body,
  );
}


export const CONTENT_CALENDAR_ARTICLE_STATUSES = [
  "draft",
  "ready_for_review",
  "approved",
  "buffer_draft_created",
  "published",
] as const;

export type ContentCalendarArticleStatus =
  (typeof CONTENT_CALENDAR_ARTICLE_STATUSES)[number];

export interface ContentCalendarArticle {
  id:
    string;

  title:
    string;

  slug:
    string;

  summary:
    string;

  articleUrl:
    string;

  excerpt:
    string | null;

  contentHtml:
    string | null;

  metaTitle:
    string | null;

  metaDescription:
    string | null;

  featuredImageUrl:
    string | null;

  featuredImageAltText:
    string | null;

  socialHook:
    string | null;

  founderPerspective:
    string | null;

  callToAction:
    string;

  hashtags:
    string[];

  status:
    ContentCalendarArticleStatus;

  generatedLinkedInText:
    string | null;

  trackedUrl:
    string | null;

  bufferChannelId:
    string | null;

  bufferPostId:
    string | null;

  approvedAt:
    string | null;

  bufferDraftCreatedAt:
    string | null;

  bufferDraftClaimedAt:
    string | null;

  bufferDraftError:
    string | null;

  bufferDraftAttemptCount:
    number;

  websitePublishedAt:
    string | null;

  publishedAt:
    string | null;

  createdAt:
    string;

  updatedAt:
    string;
}

export interface ContentCalendarArticleDraftInput {
  title:
    string;

  slug:
    string;

  summary:
    string;

  articleUrl:
    string;

  excerpt?:
    string;

  contentHtml?:
    string;

  metaTitle?:
    string;

  metaDescription?:
    string;

  featuredImageUrl:
    string;

  featuredImageAltText:
    string;

  socialHook?:
    string;

  founderPerspective?:
    string;

  callToAction?:
    string;

  hashtags?:
    string[];
}

export interface CreateContentCalendarArticleDraftInput {
  calendarItemId:
    string;

  notes?:
    string;

  article:
    ContentCalendarArticleDraftInput;
}

export interface ContentCalendarArticleCreatedResponse {
  ok:
    true;

  mode:
    "content-calendar-article-created";

  article:
    ContentCalendarArticle;

  calendarItem:
    ContentCalendarItem;
}

function parseRequiredArticleText(
  value:
    unknown,

  fieldName:
    string,
): string {
  if (
    typeof value !==
      "string" ||
    !value.trim()
  ) {
    throw new Error(
      `The article response contains an invalid ${fieldName}.`,
    );
  }

  return value;
}

function parseNullableArticleText(
  value:
    unknown,

  fieldName:
    string,
): string | null {
  if (
    value ===
      null ||
    value ===
      undefined
  ) {
    return null;
  }

  if (
    typeof value !==
      "string"
  ) {
    throw new Error(
      `The article response contains an invalid ${fieldName}.`,
    );
  }

  return value;
}

function parseArticleHashtags(
  value:
    unknown,
): string[] {
  if (
    !Array.isArray(
      value,
    ) ||
    value.some(
      (hashtag) =>
        typeof hashtag !==
          "string" ||
        !hashtag.trim(),
    )
  ) {
    throw new Error(
      "The article response contains invalid hashtags.",
    );
  }

  return [
    ...value,
  ];
}

function parseContentCalendarArticle(
  value:
    unknown,
): ContentCalendarArticle {
  if (
    !isRecord(
      value,
    ) ||
    !CONTENT_CALENDAR_ARTICLE_STATUSES.includes(
      value.status as ContentCalendarArticleStatus,
    ) ||
    typeof value.bufferDraftAttemptCount !==
      "number" ||
    !Number.isInteger(
      value.bufferDraftAttemptCount,
    ) ||
    value.bufferDraftAttemptCount <
      0
  ) {
    throw new Error(
      "The calendar article response is invalid.",
    );
  }

  return {
    id:
      parseRequiredArticleText(
        value.id,
        "id",
      ),

    title:
      parseRequiredArticleText(
        value.title,
        "title",
      ),

    slug:
      parseRequiredArticleText(
        value.slug,
        "slug",
      ),

    summary:
      parseRequiredArticleText(
        value.summary,
        "summary",
      ),

    articleUrl:
      parseRequiredArticleText(
        value.articleUrl,
        "articleUrl",
      ),

    excerpt:
      parseNullableArticleText(
        value.excerpt,
        "excerpt",
      ),

    contentHtml:
      parseNullableArticleText(
        value.contentHtml,
        "contentHtml",
      ),

    metaTitle:
      parseNullableArticleText(
        value.metaTitle,
        "metaTitle",
      ),

    metaDescription:
      parseNullableArticleText(
        value.metaDescription,
        "metaDescription",
      ),

    featuredImageUrl:
      parseNullableArticleText(
        value.featuredImageUrl,
        "featuredImageUrl",
      ),

    featuredImageAltText:
      parseNullableArticleText(
        value.featuredImageAltText,
        "featuredImageAltText",
      ),

    socialHook:
      parseNullableArticleText(
        value.socialHook,
        "socialHook",
      ),

    founderPerspective:
      parseNullableArticleText(
        value.founderPerspective,
        "founderPerspective",
      ),

    callToAction:
      parseRequiredArticleText(
        value.callToAction,
        "callToAction",
      ),

    hashtags:
      parseArticleHashtags(
        value.hashtags,
      ),

    status:
      value.status as ContentCalendarArticleStatus,

    generatedLinkedInText:
      parseNullableArticleText(
        value.generatedLinkedInText,
        "generatedLinkedInText",
      ),

    trackedUrl:
      parseNullableArticleText(
        value.trackedUrl,
        "trackedUrl",
      ),

    bufferChannelId:
      parseNullableArticleText(
        value.bufferChannelId,
        "bufferChannelId",
      ),

    bufferPostId:
      parseNullableArticleText(
        value.bufferPostId,
        "bufferPostId",
      ),

    approvedAt:
      parseNullableArticleText(
        value.approvedAt,
        "approvedAt",
      ),

    bufferDraftCreatedAt:
      parseNullableArticleText(
        value.bufferDraftCreatedAt,
        "bufferDraftCreatedAt",
      ),

    bufferDraftClaimedAt:
      parseNullableArticleText(
        value.bufferDraftClaimedAt,
        "bufferDraftClaimedAt",
      ),

    bufferDraftError:
      parseNullableArticleText(
        value.bufferDraftError,
        "bufferDraftError",
      ),

    bufferDraftAttemptCount:
      value.bufferDraftAttemptCount,

    websitePublishedAt:
      parseNullableArticleText(
        value.websitePublishedAt,
        "websitePublishedAt",
      ),

    publishedAt:
      parseNullableArticleText(
        value.publishedAt,
        "publishedAt",
      ),

    createdAt:
      parseRequiredArticleText(
        value.createdAt,
        "createdAt",
      ),

    updatedAt:
      parseRequiredArticleText(
        value.updatedAt,
        "updatedAt",
      ),
  };
}

export function parseContentCalendarArticleCreatedResponse(
  value:
    unknown,
): ContentCalendarArticleCreatedResponse {
  if (
    !isRecord(
      value,
    ) ||
    value.ok !==
      true ||
    value.mode !==
      "content-calendar-article-created"
  ) {
    throw new Error(
      "The calendar article creation response is invalid.",
    );
  }

  const article =
    parseContentCalendarArticle(
      value.article,
    );

  const calendarItem =
    parseContentCalendarItem(
      value.calendarItem,
    );

  if (
    article.status !==
      "draft" ||
    calendarItem.status !==
      "article_linked" ||
    calendarItem.articleId !==
      article.id ||
    !calendarItem.articleLinkedAt
  ) {
    throw new Error(
      "The calendar article creation response contains an invalid workflow state.",
    );
  }

  return {
    ok:
      true,

    mode:
      "content-calendar-article-created",

    article,

    calendarItem,
  };
}

export async function createContentCalendarArticleDraft(
  input:
    CreateContentCalendarArticleDraftInput,

  signal?:
    AbortSignal,
): Promise<ContentCalendarArticleCreatedResponse> {
  const calendarItemId =
    input.calendarItemId.trim();

  if (
    !calendarItemId
  ) {
    throw new Error(
      "Calendar item ID is required.",
    );
  }

  const article =
    input.article;

  const hashtags =
    article.hashtags
      ?.map(
        (hashtag) =>
          hashtag.trim(),
      )
      .filter(
        Boolean,
      );

  const response =
    await fetch(
      `/api/content-calendar/${encodeURIComponent(
        calendarItemId,
      )}/create-article`,
      {
        method:
          "POST",

        headers: {
          Accept:
            "application/json",

          "Content-Type":
            "application/json",
        },

        cache:
          "no-store",

        signal,

        body:
          JSON.stringify({
            notes:
              optionalRequestText(
                input.notes,
              ),

            article: {
              title:
                article.title.trim(),

              slug:
                article.slug.trim(),

              summary:
                article.summary.trim(),

              articleUrl:
                article.articleUrl.trim(),

              excerpt:
                optionalRequestText(
                  article.excerpt,
                ),

              contentHtml:
                optionalRequestText(
                  article.contentHtml,
                ),

              metaTitle:
                optionalRequestText(
                  article.metaTitle,
                ),

              metaDescription:
                optionalRequestText(
                  article.metaDescription,
                ),

              featuredImageUrl:
                article.featuredImageUrl.trim(),

              featuredImageAltText:
                article.featuredImageAltText.trim(),

              socialHook:
                optionalRequestText(
                  article.socialHook,
                ),

              founderPerspective:
                optionalRequestText(
                  article.founderPerspective,
                ),

              callToAction:
                optionalRequestText(
                  article.callToAction,
                ),

              hashtags:
                hashtags?.length
                  ? hashtags
                  : undefined,
            },
          }),
      },
    );

  const body =
    await parseApiResponse(
      response,
    );

  if (
    !response.ok
  ) {
    throw new Error(
      readApiError(
        body,
        `Calendar article creation failed with HTTP ${response.status}.`,
      ),
    );
  }

  return parseContentCalendarArticleCreatedResponse(
    body,
  );
}
